import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { ShieldCheck, ShieldAlert, ShieldOff } from "lucide-react";
import { api } from "../lib/api";

const CONFIG = {
  ok: {
    icon: ShieldCheck,
    color: "text-emerald-300",
    border: "border-emerald-500/40",
    bg: "bg-emerald-500/5 hover:bg-emerald-500/10",
    label: "SEC_OK",
  },
  warn: {
    icon: ShieldAlert,
    color: "text-amber-300",
    border: "border-amber-400",
    bg: "bg-amber-500/10 hover:bg-amber-500/20",
    label: "SEC_WARN",
  },
  alert: {
    icon: ShieldOff,
    color: "text-rose-300",
    border: "border-rose-400",
    bg: "bg-rose-500/10 hover:bg-rose-500/20 animate-pulse",
    label: "SEC_ALERT",
  },
};

/**
 * SecurityMarker — a persistent chip rendered inside the Layout header on every
 * authenticated page. It surfaces IP / device anomalies so the user can spot
 * mis-entries at a glance. Links to /security.
 */
export default function SecurityMarker() {
  const { data } = useQuery({
    queryKey: ["sec-summary"],
    queryFn: () => api.get("/security/summary").then((r) => r.data).catch(() => null),
    refetchInterval: 60000,
    retry: false,
  });
  if (!data) return null;
  const cfg = CONFIG[data.status] || CONFIG.ok;
  const Icon = cfg.icon;
  const tooltip = [
    `Last login: ${data.last_login?.at?.slice(0, 19) || "—"}`,
    `IP: ${data.last_login?.ip || "—"}`,
    `Recent IPs (7d): ${(data.recent_ips_7d || []).length}`,
    `Unknown devices (7d): ${data.unknown_devices_7d ?? 0}`,
    `Failed logins (7d): ${data.failed_logins_7d ?? 0}`,
    `Active alerts: ${data.active_alerts ?? 0}`,
  ].join("\n");
  return (
    <Link
      to="/security"
      data-testid="security-marker"
      title={tooltip}
      className={`inline-flex items-center gap-1.5 h-7 px-2 border ${cfg.border} ${cfg.bg} ${cfg.color} font-mono text-[10px] uppercase tracking-wider transition`}
    >
      <Icon size={11} />
      <span data-testid="security-marker-label">{cfg.label}</span>
      {data.status !== "ok" && (
        <span
          data-testid="security-marker-count"
          className="ml-1 tabnum text-[10px] font-bold"
        >
          {(data.active_alerts || 0) + (data.unknown_devices_7d || 0)}
        </span>
      )}
    </Link>
  );
}
